import { Injectable, inject } from '@angular/core';
import {
  DocumentMetadataFieldNames,
  DocumentMetadataResponse,
  SearchDocumentMetadataQuery,
  SearchDocumentMetadataQueryHandler,
} from '@okcargo/query-processor';
import { transformToSearchOptions } from '../query-repository/query.mapper';
import {
  FilterCombinator,
  FilterComparator,
  type FilterGroupPayload,
  type FilterOptions,
} from '../query-repository/query.model';
import { BaseQueryRepository } from '../query-repository/query.repository';
import { normalizeFilterCombinators } from '../query-repository/query.utils';

@Injectable({
  providedIn: 'root',
})
export class DocumentMetadataQueryService {
  private readonly queryRepository = inject(BaseQueryRepository);

  private readonly searchDocumentMetadataQueryHandler: SearchDocumentMetadataQueryHandler;

  constructor() {
    this.searchDocumentMetadataQueryHandler = new SearchDocumentMetadataQueryHandler(this.queryRepository);
  }

  public searchDocumentMetadata(query: SearchDocumentMetadataQuery): Promise<DocumentMetadataResponse[]> {
    return this.searchDocumentMetadataQueryHandler.handle(query);
  }

  // Decoupled search for the most common document metadata filters.
  // The underlying query returns a plain list, without pagination metadata.
  public async searchDocuments(options: {
    aggregateId?: string;
    type?: string;
    pageIndex?: number;
    pageSize?: number;
    abortSignal?: AbortSignal;
  }): Promise<DocumentMetadataResponse[]> {
    const filters: FilterOptions[] = [];

    if (options.aggregateId) {
      filters.push(this.eqFilter(DocumentMetadataFieldNames.AGGREGATE_ID, options.aggregateId));
    }
    if (options.type) {
      filters.push(this.eqFilter(DocumentMetadataFieldNames.TYPE, options.type));
    }

    const normalizedFilters = normalizeFilterCombinators(filters);

    return this.searchDocumentMetadataQueryHandler.handle(
      transformToSearchOptions({
        filters: normalizedFilters,
        pageIndex: options.pageIndex,
        pageSize: options.pageSize,
        abortSignal: options.abortSignal,
      }) as unknown as SearchDocumentMetadataQuery,
    );
  }

  private eqFilter(field: string, value: string): FilterOptions {
    return {
      field,
      comparator: FilterComparator.EQ,
      value,
      // Default combinator; normalized afterwards so that only the first filter is NONE
      combinator: FilterCombinator.AND,
      group: null as unknown as FilterGroupPayload,
    };
  }
}
